import { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';

export interface Profile {
  id: string;
  tenant_id: string | null;
  role: 'staff' | 'admin' | 'super_admin';
  full_name: string | null;
  vat_id?: string | null;
  billing_company_name?: string | null;
  billing_address?: string | null;
  billing_city?: string | null;
  billing_zip?: string | null;
  billing_country?: string | null;
  lock_timeout?: number;
  default_location_id?: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string, tenantId?: string | null) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchingUserId = useRef<string | null>(null);
  const profileRef = useRef<Profile | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  const fetchProfile = useCallback(async (userId: string, retryCount = 0) => {
    // Prevent redundant concurrent fetches for the same user
    if (fetchingUserId.current === userId && retryCount === 0) return;

    logger.debug('Fetching profile for user', { userId, attempt: retryCount + 1, component: 'useAuth' });
    fetchingUserId.current = userId;

    try {
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      const timeoutPromise = new Promise<{data: null, error: Error}>((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timeout')), 15000)
      );

      const result = await Promise.race([profilePromise, timeoutPromise]);
      const { data, error } = result as { data: Profile | null, error: Error | null };

      if (error) {
        logger.error('Error fetching profile', { error, userId, retryCount, component: 'useAuth' });

        // Automatic retry logic for timeouts or network errors
        if (retryCount < 2) {
          fetchingUserId.current = null; // Clear to allow retry
          logger.debug('Retrying profile fetch', { retryCount: retryCount + 1, component: 'useAuth' });
          await new Promise(resolve => setTimeout(resolve, 2000));
          return fetchProfile(userId, retryCount + 1);
        }

        // After 3 retries, show user-facing error
        toast.error('Unable to load profile. Please refresh the page.');
        return;
      }

      logger.debug('Profile fetched successfully', { userId, component: 'useAuth' });
      if (data) {
        setProfile(data as Profile);
      } else {
        setProfile(null);
      }
    } catch (err) {
      logger.error('Unexpected error fetching profile', { error: err, userId, retryCount, component: 'useAuth' });
      // Retry logic for caught errors too
      if (retryCount < 2) {
        fetchingUserId.current = null;
        await new Promise(resolve => setTimeout(resolve, 2000));
        return fetchProfile(userId, retryCount + 1);
      }

      // After 3 retries, show user-facing error
      toast.error('Unable to load profile. Please refresh the page.');
    } finally {
      if (fetchingUserId.current === userId) {
        fetchingUserId.current = null;
      }
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      // 1. Get initial session
      const { data: { session: initialSession } } = await supabase.auth.getSession();
      
      if (mounted) {
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        
        if (initialSession?.user) {
          await fetchProfile(initialSession.user.id);
        } else {
          setProfile(null);
        }
        
        setLoading(false);
      }

      // 2. Listen for changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (!mounted) return;

          setSession(session);
          setUser(session?.user ?? null);

          if (session?.user) {
            // For OAuth users, ensure profile exists (may be first login)
            if (event === 'SIGNED_IN' && session.user.app_metadata?.provider === 'google') {
              // Profile should be created by DB trigger, just fetch it
              await fetchProfile(session.user.id);
            } else if (!profileRef.current || profileRef.current.id !== session.user.id) {
              // Only show loading if we don't have a profile yet or if the user changed
              // We don't necessarily want to set loading(true) here as it might flicker the UI
              // but we do need to ensure we have the profile.
              await fetchProfile(session.user.id);
            }
          } else {
            setProfile(null);
          }

          setLoading(false);
        }
      );

      return subscription;
    };

    const authInitPromise = initializeAuth();

    return () => {
      mounted = false;
      authInitPromise.then(sub => sub.unsubscribe());
    };
  }, [fetchProfile]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName: string, tenantId?: string | null) => {
    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          tenant_id: tenantId || null,
        },
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
